import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

// ============================================
// Types
// ============================================

export type DisputeStatus =
  | 'PENDING_OCR'
  | 'PENDING_VERIFICATION'
  | 'VERIFIED'
  | 'UNVERIFIED'
  | 'OCR_FAILED'
  | 'DUPLICATE_IMAGE'
  | 'DUPLICATE_E2E'
  | 'PENDING_REVIEW'
  | 'PENDING_SECOND_APPROVAL'
  | 'APPROVED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'ALREADY_CREDITED';

export type DisputeType = 'DEPOSIT' | 'TRANSFER';

export type RejectionReason =
  | 'ILLEGIBLE'
  | 'FAKE'
  | 'ALREADY_CREDITED'
  | 'NOT_FOUND'
  | 'DATA_MISMATCH'
  | 'OTHER';

export interface DisputeSummary {
  id: string;
  status: DisputeStatus;
  claimedAmountCents: number;
  claimedDate: string;
  claimedType: DisputeType;
  createdAt: string;
  updatedAt: string;
}

export interface DisputeDetail extends DisputeSummary {
  imagePath: string;
  imageHash: string;
  ocrData?: Record<string, unknown>;
  ocrConfidence?: number;
  extractedAmountCents?: number;
  extractedDate?: string;
  extractedE2eId?: string;
  extractedPayerDoc?: string;
  extractedPixKey?: string;
  anypayVerification?: Record<string, unknown>;
  anypayTransactionId?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  rejectionReason?: RejectionReason;
  rejectionReasonDetail?: string;
  creditedAmountCents?: number;
  creditedAt?: string;
  feeCents?: number;
  netAmountCents?: number;
  duplicateOfId?: string;
}

export interface DisputeAdminDetail extends DisputeDetail {
  tenantId: string;
  userId: string;
  virtualAccountId: string;
  user?: { name: string; email: string; cpf?: string };
  virtualAccount?: { id: string; balance: number };
  history?: DisputeTimelineEntry[];
}

export interface DisputeCounters {
  PENDING_OCR: number;
  PENDING_VERIFICATION: number;
  VERIFIED: number;
  UNVERIFIED: number;
  OCR_FAILED: number;
  DUPLICATE_IMAGE: number;
  DUPLICATE_E2E: number;
  PENDING_REVIEW: number;
  PENDING_SECOND_APPROVAL: number;
  APPROVED: number;
  REJECTED: number;
  CANCELLED: number;
  ALREADY_CREDITED: number;
  total: number;
  pending: number;
}

export interface DisputeTimelineEntry {
  id: string;
  fromStatus?: DisputeStatus;
  toStatus: DisputeStatus;
  action: string;
  changedBy?: string;
  changedAt: string;
  notes?: string;
  metadata?: Record<string, unknown>;
}

export interface DisputeQueryParams {
  page?: number;
  limit?: number;
  status?: DisputeStatus;
  startDate?: string;
  endDate?: string;
  minAmount?: number;
  maxAmount?: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface AuditSummary {
  totalDisputes: number;
  totalApproved: number;
  totalRejected: number;
  totalCreditedCents: number;
  totalFeeCents: number;
  averageResolutionHours: number;
  disputesByStatus: Record<string, number>;
  disputesByMonth: { month: string; count: number }[];
}

// ============================================
// Banking (Correntista) Hooks
// ============================================

export function useCreateDispute() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, claimedAmountCents, claimedDate, claimedType }: {
      file: File;
      claimedAmountCents: number;
      claimedDate: string;
      claimedType: DisputeType;
    }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('claimedAmountCents', String(claimedAmountCents));
      formData.append('claimedDate', claimedDate);
      formData.append('claimedType', claimedType);
      const { data } = await api.post('/banking/disputes', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-disputes'] });
    },
  });
}

export function useMyDisputes(query: { page?: number; limit?: number } = {}) {
  return useQuery({
    queryKey: ['my-disputes', query],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (query.page) params.append('page', String(query.page));
      if (query.limit) params.append('limit', String(query.limit));
      const { data } = await api.get(`/banking/disputes?${params.toString()}`);
      return data as PaginatedResponse<DisputeSummary>;
    },
  });
}

export function useDisputeDetail(id: string) {
  return useQuery({
    queryKey: ['dispute-detail', id],
    queryFn: async () => {
      const { data } = await api.get(`/banking/disputes/${id}`);
      return data as DisputeDetail;
    },
    enabled: !!id,
  });
}

// ============================================
// Admin Hooks
// ============================================

export function useAdminDisputes(query: DisputeQueryParams = {}) {
  return useQuery({
    queryKey: ['admin-disputes', query],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (query.page) params.append('page', String(query.page));
      if (query.limit) params.append('limit', String(query.limit));
      if (query.status) params.append('status', query.status);
      if (query.startDate) params.append('startDate', query.startDate);
      if (query.endDate) params.append('endDate', query.endDate);
      if (query.minAmount) params.append('minAmount', String(query.minAmount));
      if (query.maxAmount) params.append('maxAmount', String(query.maxAmount));
      const { data } = await api.get(`/admin/disputes?${params.toString()}`);
      return data as PaginatedResponse<DisputeAdminDetail>;
    },
  });
}

export function useDisputeCounters() {
  return useQuery({
    queryKey: ['dispute-counters'],
    queryFn: async () => {
      const { data } = await api.get('/admin/disputes/counters');
      return data as DisputeCounters;
    },
    refetchInterval: 30000, // Refresh every 30s
  });
}

export function useAdminDisputeDetail(id: string) {
  return useQuery({
    queryKey: ['admin-dispute-detail', id],
    queryFn: async () => {
      const { data } = await api.get(`/admin/disputes/${id}`);
      return data as DisputeAdminDetail;
    },
    enabled: !!id,
  });
}

export function useDisputeImage(id: string) {
  return useQuery({
    queryKey: ['dispute-image', id],
    queryFn: async () => {
      const { data } = await api.get(`/admin/disputes/${id}/image`, {
        responseType: 'blob',
      });
      return URL.createObjectURL(data);
    },
    enabled: !!id,
  });
}

export function useDisputeTimeline(id: string) {
  return useQuery({
    queryKey: ['dispute-timeline', id],
    queryFn: async () => {
      const { data } = await api.get(`/admin/disputes/${id}/timeline`);
      return data as DisputeTimelineEntry[];
    },
    enabled: !!id,
  });
}

export function useApproveDispute() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, notes, confirmed }: { id: string; notes: string; confirmed: boolean }) => {
      const { data } = await api.post(`/admin/disputes/${id}/approve`, { notes, confirmed });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-disputes'] });
      queryClient.invalidateQueries({ queryKey: ['admin-dispute-detail'] });
      queryClient.invalidateQueries({ queryKey: ['dispute-counters'] });
    },
  });
}

export function useRejectDispute() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, notes, reason, reasonDetail }: {
      id: string;
      notes: string;
      reason: RejectionReason;
      reasonDetail?: string;
    }) => {
      const { data } = await api.post(`/admin/disputes/${id}/reject`, { notes, reason, reasonDetail });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-disputes'] });
      queryClient.invalidateQueries({ queryKey: ['admin-dispute-detail'] });
      queryClient.invalidateQueries({ queryKey: ['dispute-counters'] });
    },
  });
}

export function useAuditSummary() {
  return useQuery({
    queryKey: ['dispute-audit-summary'],
    queryFn: async () => {
      const { data } = await api.get('/admin/disputes/audit-summary');
      return data as AuditSummary;
    },
  });
}

// ============================================
// Admin Intervention Hooks
// ============================================

export function useReprocessOcr() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/admin/disputes/${id}/reprocess-ocr`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-disputes'] });
      queryClient.invalidateQueries({ queryKey: ['admin-dispute-detail'] });
      queryClient.invalidateQueries({ queryKey: ['dispute-counters'] });
      queryClient.invalidateQueries({ queryKey: ['dispute-timeline'] });
    },
  });
}

export function useForceVerify() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/admin/disputes/${id}/force-verify`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-disputes'] });
      queryClient.invalidateQueries({ queryKey: ['admin-dispute-detail'] });
      queryClient.invalidateQueries({ queryKey: ['dispute-counters'] });
      queryClient.invalidateQueries({ queryKey: ['dispute-timeline'] });
    },
  });
}

export function useAddDisputeNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, note }: { id: string; note: string }) => {
      const { data } = await api.post(`/admin/disputes/${id}/note`, { note });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dispute-timeline'] });
      queryClient.invalidateQueries({ queryKey: ['admin-dispute-detail'] });
    },
  });
}

export function useReverseDispute() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, notes, confirmed }: { id: string; notes: string; confirmed: boolean }) => {
      const { data } = await api.post(`/admin/disputes/${id}/reverse`, { notes, confirmed });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-disputes'] });
      queryClient.invalidateQueries({ queryKey: ['admin-dispute-detail'] });
      queryClient.invalidateQueries({ queryKey: ['dispute-counters'] });
      queryClient.invalidateQueries({ queryKey: ['dispute-timeline'] });
    },
  });
}

export function useDetailedLog(id: string) {
  return useQuery({
    queryKey: ['dispute-detailed-log', id],
    queryFn: async () => {
      const { data } = await api.get(`/admin/disputes/${id}/detailed-log`);
      return data;
    },
    enabled: !!id,
  });
}
